import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { isAuthenticated } from './utils/auth.js';
import { api } from './utils/api.js';
import PageTransition from './components/shared/PageTransition.jsx';
import Login from './pages/Login.jsx';
import Signup from './pages/Signup.jsx';
import Dashboard from './pages/Dashboard.jsx';
import Upload from './pages/Upload.jsx';
import ReviewTable from './pages/ReviewTable.jsx';
import Export from './pages/Export.jsx';
import Settings from './pages/Settings.jsx';
import ResetPassword from './pages/ResetPassword.jsx';
import ForgotPassword from './pages/ForgotPassword.jsx';
import AuthCallback from './pages/AuthCallback.jsx';
import Admin from './pages/Admin.jsx';
import AdminProposals from './pages/AdminProposals.jsx';
import { DropDetail, DropList } from './pages/CatalogDrops.js';
import OfflineToast from './components/shared/OfflineToast.js';
import { Lock } from 'lucide-react';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  if (isAuthenticated()) return <Navigate to="/dashboard" replace />;
  return children;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<'loading' | 'admin' | 'denied'>('loading');

  useEffect(() => {
    if (!isAuthenticated()) { setState('denied'); return; }
    api.getMe()
      .then((data: any) => setState(data.is_admin ? 'admin' : 'denied'))
      .catch(() => setState('denied'));
  }, []);

  if (state === 'loading') return null;
  if (state === 'denied') return <Navigate to="/dashboard" replace />;
  return children;
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/login" element={
          <PublicRoute><PageTransition><Login /></PageTransition></PublicRoute>
        } />
        <Route path="/signup" element={
          <PublicRoute><PageTransition><Signup /></PageTransition></PublicRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute><PageTransition><Dashboard /></PageTransition></ProtectedRoute>
        } />
        <Route path="/upload" element={
          <ProtectedRoute><PageTransition><Upload /></PageTransition></ProtectedRoute>
        } />
        {/* /processing removed — in-progress catalogs go to /review with ?job_id= */}
        <Route path="/review/:catalogId" element={
          <ProtectedRoute><PageTransition><ReviewTable /></PageTransition></ProtectedRoute>
        } />
        <Route path="/export/:catalogId" element={
          <ProtectedRoute><PageTransition><Export /></PageTransition></ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute><PageTransition><Settings /></PageTransition></ProtectedRoute>
        } />
        <Route path="/admin" element={
          <AdminRoute><PageTransition><Admin /></PageTransition></AdminRoute>
        } />
        <Route path="/admin/proposals" element={
          <AdminRoute><PageTransition><AdminProposals /></PageTransition></AdminRoute>
        } />
        <Route path="/drops" element={
          <ProtectedRoute><PageTransition><DropList /></PageTransition></ProtectedRoute>
        } />
        <Route path="/drops/:catalog_drop_id" element={
          <ProtectedRoute><PageTransition><DropDetail /></PageTransition></ProtectedRoute>
        } />
        <Route path="/reset-password-request" element={
          <PageTransition><ForgotPassword /></PageTransition>
        } />
        <Route path="/reset-password" element={
          <PageTransition><ResetPassword /></PageTransition>
        } />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AnimatePresence>
  );
}

function SessionExpiredModal() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    function handle() { setVisible(true); }
    window.addEventListener('cc:sessionExpired', handle);
    return () => window.removeEventListener('cc:sessionExpired', handle);
  }, []);

  if (!visible) return null;

  const redirect = encodeURIComponent(window.location.pathname + window.location.search);
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'var(--color-surface)', borderRadius: 16, padding: '36px 40px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)', maxWidth: 360, width: '100%',
        textAlign: 'center',
      }}>
        <div style={{ marginBottom: 12 }}><Lock size={32} style={{ color: 'var(--color-text-2)' }} /></div>
        <div style={{ fontWeight: 700, fontSize: 17, color: 'var(--color-text)', marginBottom: 8 }}>
          Session expired
        </div>
        <div style={{ fontSize: 14, color: 'var(--color-text-2)', marginBottom: 24, lineHeight: 1.5 }}>
          Your session has timed out. Sign in again to keep working — your data is safe.
        </div>
        <a
          href={`/login?redirect=${redirect}`}
          style={{
            display: 'inline-block', padding: '10px 28px',
            background: 'var(--color-primary)', color: '#fff',
            borderRadius: 8, fontWeight: 600, fontSize: 14,
            textDecoration: 'none',
          }}
        >
          Sign in again
        </a>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <SessionExpiredModal />
      <OfflineToast />
      <AnimatedRoutes />
    </BrowserRouter>
  );
}
